import type {
  MultiChartDataPoint,
  ReferencedMultiChartDataPoint,
  SummaryPanelProps,
  DataTableColumn,
  LegacyTableRow,
} from "@wayflyer/flyui-dataviz";
import { styled } from "styled-components";
import * as React from "react";
import { roundToNumDecimals } from "shared-utils";
// eslint-disable-next-line no-restricted-imports
import moment, { type Moment } from "moment";
import { isNaN } from "lodash";
import type { IntlShape } from "react-intl";
import { Text } from "@wayflyer/flyui";

import { MetricTypes } from "../../autogen/metrics";
import { LtvTimeKeys } from "../../autogen/ltv-data";
import type { DashboardOutput, DataPoint } from "../../utils/fetch-api";
import { formatValue } from "../../strings/type-formatting";
import { getSort } from "../../utils/metrics";
import { ltvCacTableColumnTooltips } from "../../strings/tooltips";

import { getNumMonthMap, isNarrowCurrency } from "./helpers";
import { TempRes } from "./constants";
import { getHeatmapColumn, type LtvRow } from "./heatmap";

const BoldDiv = styled.div`
  font-weight: bold;
`;

export interface ProcessedLtvCacData {
  columns: DataTableColumn<LtvRow>[];
  data: LtvRow[];
  ltvAverages: { [key: string]: number };
  ltvQuartiles: number[];
}

// Convert a string of the form YYYY - {int} to two numbers
const convertStringDate = (date: string) => {
  const [year, month] = date.split(" - ");
  return [parseInt(year), parseInt(month)];
};

const filterDataBase = (data, getDateParts, tempRes: TempRes, startDate: string, endDate: string) => {
  const [startYear, startMonth] = convertStringDate(startDate);
  const [endYear, endMonth] = convertStringDate(endDate);

  return data.filter((row) => {
    const { year, month } = getDateParts(row);

    if (year < startYear) {
      return false;
    }
    if (year === startYear && year === endYear) {
      return month >= startMonth && month <= endMonth;
    }
    if (year === startYear) {
      return month >= startMonth;
    }
    if (year === endYear) {
      return month <= endMonth;
    }
    return year <= endYear;
  });
};

const MIN_VAL_TO_SHOW_DECIMALS = 10;

const getFormattedTime = (
  year: number | string,
  monthOrQuarter: number | string,
  tempRes: TempRes,
  monthMap
): string => {
  if (tempRes === TempRes.Month) {
    return `${monthMap[monthOrQuarter]}-${year}`;
  } else {
    return `Q${monthOrQuarter}-${year}`;
  }
};

const getFormattedLTVMomentForDisplay = (date: Moment, monthly: boolean) => {
  return date.format(monthly ? "MMM-YYYY" : "[Q]Q-YYYY");
};

const getFormattedLTVMomentForSort = (date: Moment, monthly: boolean) => {
  return date.format(monthly ? "YYYY - M" : "YYYY - Q");
};

const extractLTVData = (data: DashboardOutput, monthly: boolean, start: Moment, end: Moment) => {
  let minLTV = Infinity;
  let maxLTV = 0;

  // First order at -> Month/quarter -> [revenue, customers]
  const averagesData = {};

  const ltvData = data.data.map((row, fcp) => {
    const day = moment(row["day"], "YYYY-MM-DD");
    const ltvRow = {};

    ltvRow[MetricTypes.FCP] = getFormattedLTVMomentForDisplay(day, monthly);
    ltvRow[getSort(MetricTypes.FCP)] = getFormattedLTVMomentForSort(day, monthly);

    ltvRow[getSort("0")] = row[MetricTypes.SAME_MONTH_LTV];
    ltvRow["0"] = formatValue({
      value: (+(row[MetricTypes.SAME_MONTH_LTV] ?? 0)).toString(),
      metricType: {
        type: "CURRENCY",
        currencyCode: data.currencyCode,
      },
      withDecimal: false,
      narrowCurrency: isNarrowCurrency(data.currencyCode),
    });

    averagesData[fcp] = {
      0: [row[MetricTypes.SAME_MONTH_NET_REVENUE], row[MetricTypes.SAME_MONTH_UNIQUE_CUSTOMER_COUNT]],
    };

    const rowRevenue = {};
    const rowCustomers = {};
    LtvTimeKeys.slice(2).forEach((key, index) => {
      if (end.diff(day, "month") > index - 1) {
        const metric = key.toUpperCase().replace(/[- ]/g, "_");
        const bucketIndex = 1 + Math.floor(index / (monthly ? 1 : 3));
        rowCustomers[bucketIndex] =
          (rowCustomers[bucketIndex] || 0) + row[MetricTypes[`${metric}_UNIQUE_CUSTOMER_COUNT`]];
        rowRevenue[bucketIndex] = (rowRevenue[bucketIndex] || 0) + row[MetricTypes[`${metric}_NET_REVENUE`]];
      }
    });
    Object.keys(rowCustomers).forEach((monthsElapsed) => {
      const ltv = rowRevenue[monthsElapsed] / rowCustomers[monthsElapsed];
      if (ltv > maxLTV) {
        maxLTV = ltv;
      }
      if (ltv < minLTV) {
        minLTV = ltv;
      }
      ltvRow[getSort(monthsElapsed)] = ltv;
      ltvRow[monthsElapsed] = formatValue({
        value: ltv.toString(),
        metricType: {
          type: "CURRENCY",
          currencyCode: data.currencyCode,
        },
        withDecimal: false,
        narrowCurrency: isNarrowCurrency(data.currencyCode),
      });

      averagesData[fcp][monthsElapsed] = [rowRevenue[monthsElapsed], rowCustomers[monthsElapsed]];
    });
    return ltvRow;
  });

  // Compute averages directly rather than relying on the summary averages which will include the full square of metrics
  // If we don't have data for a square, then assume the worst case, i.e. LTV remains fixed to ensure avg increases
  const ltvAverages = {};
  if (Object.keys(averagesData).length > 0) {
    Object.keys(averagesData[0]).forEach((monthsElapsed) => {
      const sums = Object.keys(averagesData).reduce(
        (agg, fcp) => {
          const latestMonth = Math.max(...Object.keys(averagesData[fcp]).map((x) => parseInt(x))).toString();
          const data = averagesData[fcp][monthsElapsed] || averagesData[fcp][latestMonth];
          return [agg[0] + data[0], agg[1] + data[1]];
        },
        [0, 0]
      );
      ltvAverages[monthsElapsed] = sums[0] / sums[1];
    });
  }

  const averageLtvRow: LtvRow = {
    [MetricTypes.FCP]: "Average",
  };
  Object.keys(ltvAverages).forEach((key) => {
    averageLtvRow[key] = formatValue({
      value: ltvAverages[key].toString(),
      metricType: {
        type: "CURRENCY",
        currencyCode: data.currencyCode,
      },
      withDecimal: false,
      narrowCurrency: isNarrowCurrency(data.currencyCode),
    });
    averageLtvRow[getSort(key)] = ltvAverages[key];
  });

  return { ltvData, ltvAverages, averageLtvRow, minLTV, maxLTV };
};

const extractCACData = (data: DashboardOutput, monthly: boolean) => {
  const cacData = data.data.map((row) => {
    const day = moment(row["day"]);
    return {
      [MetricTypes.FCP]: getFormattedLTVMomentForDisplay(day, monthly),
      [getSort(MetricTypes.FCP)]: getFormattedLTVMomentForSort(day, monthly),
      [getSort(MetricTypes.ALLTIME_CAC)]: row[MetricTypes.ALLTIME_CAC] as number,
      [getSort(MetricTypes.ALLTIME_AOV)]: row[MetricTypes.ALLTIME_AOV] as number,
      [MetricTypes.ALLTIME_UNIQUE_CUSTOMER_COUNT]: row[MetricTypes.ALLTIME_UNIQUE_CUSTOMER_COUNT] as number,
    };
  });

  const cacDecimalShow = cacData.some(
    (row) => Number(row[getSort(MetricTypes.ALLTIME_CAC)]) < MIN_VAL_TO_SHOW_DECIMALS
  );
  const aovDecimalShow = cacData.some(
    (row) => Number(row[getSort(MetricTypes.ALLTIME_AOV)]) < MIN_VAL_TO_SHOW_DECIMALS
  );

  cacData.forEach((row) => {
    row[MetricTypes.ALLTIME_CAC] = formatValue({
      value: (+(row[getSort(MetricTypes.ALLTIME_CAC)] ?? 0)).toString(),
      metricType: {
        type: "CURRENCY",
        currencyCode: data.currencyCode,
      },
      withDecimal: cacDecimalShow,
      narrowCurrency: isNarrowCurrency(data.currencyCode),
    });

    row[MetricTypes.ALLTIME_AOV] = formatValue({
      value: (+(row[getSort(MetricTypes.ALLTIME_AOV)] ?? 0)).toString(),
      metricType: {
        type: "CURRENCY",
        currencyCode: data.currencyCode,
      },
      withDecimal: aovDecimalShow,
      narrowCurrency: isNarrowCurrency(data.currencyCode),
    });
  });

  const summary = data.summary;

  const averageUCC = +(summary[MetricTypes.ALLTIME_UNIQUE_CUSTOMER_COUNT] ?? 0) / cacData.length;
  const averageAOV = +(summary[MetricTypes.ALLTIME_AOV] ?? 0);
  const averageCAC = +(summary[MetricTypes.ALLTIME_CAC] ?? 0);
  const averageCacRow: LtvRow = {
    [MetricTypes.FCP]: "Average",
    [MetricTypes.ALLTIME_UNIQUE_CUSTOMER_COUNT]: roundToNumDecimals(averageUCC, 0).toLocaleString(),
    [getSort(MetricTypes.ALLTIME_UNIQUE_CUSTOMER_COUNT)]: averageUCC,
    [MetricTypes.ALLTIME_CAC]: formatValue({
      value: averageCAC.toString(),
      metricType: {
        type: "CURRENCY",
        currencyCode: data.currencyCode,
      },
      withDecimal: cacDecimalShow,
      narrowCurrency: isNarrowCurrency(data.currencyCode),
    }),
    [getSort(MetricTypes.ALLTIME_CAC)]: averageCAC,
    [MetricTypes.ALLTIME_AOV]: formatValue({
      value: averageAOV.toString(),
      metricType: {
        type: "CURRENCY",
        currencyCode: data.currencyCode,
      },
      withDecimal: aovDecimalShow,
      narrowCurrency: isNarrowCurrency(data.currencyCode),
    }),
    [getSort(MetricTypes.ALLTIME_AOV)]: averageAOV,
  };

  return { cacData, averageCacRow };
};

const getCacCols = (intl: IntlShape): DataTableColumn<LtvRow>[] => {
  const cacColNames = {
    [MetricTypes.FCP]: MetricTypes.FCP,
    [MetricTypes.ALLTIME_UNIQUE_CUSTOMER_COUNT]: MetricTypes.NEW_CUSTOMERS,
    [MetricTypes.ALLTIME_CAC]: MetricTypes.CAC,
    // [MetricTypes.ALLTIME_AOV]: MetricTypes.AOV,
  };
  // TODO: Remove when i18n is nicely applied to metrics
  const hackyI18N = {
    [MetricTypes.FCP]: { id: MetricTypes.FCP, defaultMessage: "First Order at" },
    [MetricTypes.ALLTIME_UNIQUE_CUSTOMER_COUNT]: {
      id: MetricTypes.ALLTIME_UNIQUE_CUSTOMER_COUNT,
      defaultMessage: "New Customers",
    },
    [MetricTypes.ALLTIME_CAC]: { id: MetricTypes.ALLTIME_CAC, defaultMessage: "Customer Acquisition Cost" },
  };
  return Object.keys(cacColNames).map((key): DataTableColumn<LtvRow> => {
    return {
      name: intl.formatMessage(hackyI18N[key]),
      id: key,
      align: "left",
      headerTooltip: ltvCacTableColumnTooltips(key),
      render: ({ row }) => (
        <Text fontWeight={key === MetricTypes.FCP || row.original.fcp === "Average" ? "semiBold" : "regular"}>
          {row.original[key]}
        </Text>
      ),
    };
  });
};

const getLtvCols = (quartiles: number[], ltvAverages, monthly: boolean, getTooltip) => {
  return Object.keys(ltvAverages).map((key) => {
    const name = (() => {
      if (key === "0") {
        return "First Order";
      } else if (monthly) {
        return `${+key - 1} mth`;
      } else {
        return `${+key - 1} qtr`;
      }
    })();
    return getHeatmapColumn(key, name, quartiles, getTooltip);
  });
};

const getCohortDay = (dataPoint: DataPoint, numberAgo: number, monthly: boolean) => {
  return moment(dataPoint.day)
    .subtract(numberAgo, monthly ? "months" : "quarter")
    .format("YYYY-MM-DD");
};

const getChartDataPointQuarterly = (data: DashboardOutput, dataPoint: DataPoint) => {
  const quartersAgo = {
    one: getCohortDay(dataPoint, 1, false),
    two: getCohortDay(dataPoint, 2, false),
    four: getCohortDay(dataPoint, 4, false),
  };
  const quartersAgoDataPoints: { one?: DataPoint; two?: DataPoint; four?: DataPoint } = {
    one: undefined,
    two: undefined,
    four: undefined,
  };
  data.data.forEach((dataPoint) => {
    switch (dataPoint.day) {
      case quartersAgo.one:
        quartersAgoDataPoints.one = dataPoint;
        break;
      case quartersAgo.two:
        quartersAgoDataPoints.two = dataPoint;
        break;
      case quartersAgo.four:
        quartersAgoDataPoints.four = dataPoint;
        break;
    }
  });
  const projectedThreeMDataPoint = quartersAgoDataPoints.one
    ? +(quartersAgoDataPoints.one[MetricTypes.FOUR_MONTH_LTV] ?? 0)
    : null;
  const projectedSixMDataPoint =
    quartersAgoDataPoints.one && quartersAgoDataPoints.two
      ? +(quartersAgoDataPoints.two[MetricTypes.SEVEN_MONTH_LTV] ?? 0) -
        +(quartersAgoDataPoints.two[MetricTypes.FOUR_MONTH_LTV] ?? 0) +
        +(quartersAgoDataPoints.one[MetricTypes.FOUR_MONTH_LTV] ?? 0)
      : null;
  const projectedTwelveMDataPoint =
    quartersAgoDataPoints.one && quartersAgoDataPoints.two && quartersAgoDataPoints.four
      ? +(quartersAgoDataPoints.four[MetricTypes.THIRTEEN_MONTH_LTV] ?? 0) -
        +(quartersAgoDataPoints.four[MetricTypes.SEVEN_MONTH_LTV] ?? 0) +
        +(quartersAgoDataPoints.two[MetricTypes.SEVEN_MONTH_LTV] ?? 0) -
        +(quartersAgoDataPoints.two[MetricTypes.FOUR_MONTH_LTV] ?? 0) +
        +(quartersAgoDataPoints.one[MetricTypes.FOUR_MONTH_LTV] ?? 0)
      : null;
  return { projectedThreeMDataPoint, projectedSixMDataPoint, projectedTwelveMDataPoint };
};

const getChartDataPointMonthly = (data: DashboardOutput, dataPoint: DataPoint) => {
  const monthsAgo = {
    three: getCohortDay(dataPoint, 3, true),
    six: getCohortDay(dataPoint, 6, true),
    twelve: getCohortDay(dataPoint, 12, true),
  };
  const monthsAgoDataPoints: { three?: DataPoint; six?: DataPoint; twelve?: DataPoint } = {
    three: undefined,
    six: undefined,
    twelve: undefined,
  };
  data.data.forEach((dataPoint) => {
    switch (dataPoint.day) {
      case monthsAgo.three:
        monthsAgoDataPoints.three = dataPoint;
        break;

      case monthsAgo.six:
        monthsAgoDataPoints.six = dataPoint;
        break;

      case monthsAgo.twelve:
        monthsAgoDataPoints.twelve = dataPoint;
        break;
    }
  });
  const projectedThreeMDataPoint = monthsAgoDataPoints.three
    ? +(monthsAgoDataPoints.three[MetricTypes.FOUR_MONTH_LTV] ?? 0)
    : null;
  const projectedSixMDataPoint = monthsAgoDataPoints.six
    ? +(monthsAgoDataPoints.six[MetricTypes.SEVEN_MONTH_LTV] ?? 0) -
      +(monthsAgoDataPoints.six[MetricTypes.FOUR_MONTH_LTV] ?? 0) +
      +(projectedThreeMDataPoint ?? 0)
    : null;
  const projectedTwelveMDataPoint = monthsAgoDataPoints.twelve
    ? +(monthsAgoDataPoints.twelve[MetricTypes.THIRTEEN_MONTH_LTV] ?? 0) -
      +(monthsAgoDataPoints.twelve[MetricTypes.SEVEN_MONTH_LTV] ?? 0) +
      +(projectedSixMDataPoint ?? 0)
    : null;
  return { projectedThreeMDataPoint, projectedSixMDataPoint, projectedTwelveMDataPoint };
};

export const generateCohortDataPoints = (
  actualMetricTypes: MetricTypes[],
  projectedMetricTypes: MetricTypes[],
  data: DashboardOutput,
  monthly: boolean
): ReferencedMultiChartDataPoint[] => {
  return data.data.map((dataPoint) => {
    const chartDataPoint = getInitialDataPointWithDay(dataPoint, monthly);

    const { projectedThreeMDataPoint, projectedSixMDataPoint, projectedTwelveMDataPoint } = monthly
      ? getChartDataPointMonthly(data, dataPoint)
      : getChartDataPointQuarterly(data, dataPoint);

    projectedMetricTypes.concat(actualMetricTypes).forEach((metricType) => {
      // if (dataPoint[metricType]) {
      switch (metricType) {
        case MetricTypes.FOUR_MONTH_LTV: {
          chartDataPoint[metricType] = projectedThreeMDataPoint;
          break;
        }
        case MetricTypes.SEVEN_MONTH_LTV: {
          chartDataPoint[metricType] = projectedSixMDataPoint;
          break;
        }
        case MetricTypes.THIRTEEN_MONTH_LTV: {
          chartDataPoint[metricType] = projectedTwelveMDataPoint;
          break;
        }
        default: {
          chartDataPoint[metricType] = dataPoint[metricType];
          break;
        }
      }
    });
    return chartDataPoint;
  });
};

const getInitialDataPointWithDay = (dataPoint: DataPoint, monthly: boolean) => {
  return monthly
    ? { day: moment(dataPoint.day).format(ltvSummaryChartDateFormat) }
    : { day: quarterFormat(dataPoint.day as string) };
};

export const generateSummaryChartData = (
  monthly: boolean,
  metricTypes: MetricTypes[],
  cohortData: ReferencedMultiChartDataPoint[],
  showThreeM: boolean,
  showSixM: boolean,
  showTwelveM: boolean
): ReferencedMultiChartDataPoint[] => {
  const nDataPoints = cohortData.length;
  if (!nDataPoints) {
    return [];
  }
  return cohortData.map((dataPoint, index) => {
    const chartDataPoint = { day: dataPoint.day };
    metricTypes.forEach((metricType) => {
      if (dataPoint[metricType]) {
        // Calc bar sizes
        const threeMBar =
          showThreeM && dataPoint[MetricTypes.THREE_MONTH_LTV] ? dataPoint[MetricTypes.THREE_MONTH_LTV] : 0;
        const sixMBar = (() => {
          const barSize = dataPoint[MetricTypes.SIX_MONTH_LTV];
          if (showSixM && dataPoint[MetricTypes.SIX_MONTH_LTV]) {
            if (showThreeM) {
              return barSize - threeMBar;
            }
            return barSize;
          } else {
            return 0;
          }
        })();
        const twelveMBar = (() => {
          const barSize = dataPoint[MetricTypes.TWELVE_MONTH_LTV];
          if (showTwelveM && dataPoint[MetricTypes.TWELVE_MONTH_LTV]) {
            if (showSixM) {
              return barSize - dataPoint[MetricTypes.SIX_MONTH_LTV];
            }
            if (showThreeM) {
              return barSize - dataPoint[MetricTypes.THREE_MONTH_LTV];
            }
            return barSize;
          } else {
            return 0;
          }
        })();
        // Calc projected bar sizes
        const threeMBarProj =
          showThreeM && dataPoint[MetricTypes.FOUR_MONTH_LTV] ? dataPoint[MetricTypes.FOUR_MONTH_LTV] : 0;
        const sixMBarProj = (() => {
          const barSize = dataPoint[MetricTypes.SEVEN_MONTH_LTV];
          if (showSixM && dataPoint[MetricTypes.SEVEN_MONTH_LTV]) {
            if (showThreeM) {
              return barSize - threeMBarProj;
            }
            return barSize;
          } else {
            return 0;
          }
        })();
        const twelveMBarProj = (() => {
          const barSize = dataPoint[MetricTypes.THIRTEEN_MONTH_LTV];
          if (showTwelveM && dataPoint[MetricTypes.THIRTEEN_MONTH_LTV]) {
            if (showSixM) {
              return barSize - dataPoint[MetricTypes.SEVEN_MONTH_LTV];
            }
            if (showThreeM) {
              return barSize - dataPoint[MetricTypes.FOUR_MONTH_LTV];
            }
            return barSize;
          } else {
            return 0;
          }
        })();
        const real3M = nDataPoints - (monthly ? 3 : 1) >= index;
        const real6M = nDataPoints - (monthly ? 6 : 2) >= index;
        const real12M = nDataPoints - (monthly ? 12 : 4) >= index;
        switch (metricType) {
          case MetricTypes.THREE_MONTH_LTV: {
            if (real3M) {
              chartDataPoint[metricType] = threeMBar;
            }
            break;
          }
          case MetricTypes.SIX_MONTH_LTV: {
            if (real6M) {
              chartDataPoint[metricType] = sixMBar;
            }
            break;
          }
          case MetricTypes.TWELVE_MONTH_LTV: {
            if (real12M) {
              chartDataPoint[metricType] = twelveMBar;
            }
            break;
          }
          case MetricTypes.FOUR_MONTH_LTV: {
            if (!real3M) {
              chartDataPoint[metricType] = threeMBarProj;
            }
            break;
          }
          case MetricTypes.SEVEN_MONTH_LTV: {
            if (!real6M) {
              chartDataPoint[metricType] = sixMBarProj;
            }
            break;
          }
          case MetricTypes.THIRTEEN_MONTH_LTV: {
            if (!real12M) {
              chartDataPoint[metricType] = twelveMBarProj;
            }
            break;
          }
          default: {
            chartDataPoint[metricType] = dataPoint[metricType];
            break;
          }
        }
      }
    });
    return chartDataPoint;
  });
};

const getSummaryTooltip = (intl: IntlShape, metricType: MetricTypes, monthly: boolean) => {
  const customerValueStr = (monthsAgo: number) => {
    return `Average net sales contributed by a customer to date who made a first order ${
      monthly ? monthsAgo + " months" : monthsAgo / 3 + " quarters"
    } ago.`;
  };
  switch (metricType) {
    case MetricTypes.ALLTIME_CAC:
      return intl.formatMessage({
        id: "Consumers Screen: Alltime CAC",
        defaultMessage:
          "Total Marketing spend divided by a number of new customers acquired through the considered period.",
      });
    case MetricTypes.ALLTIME_AOV:
      return intl.formatMessage({
        id: "Consumers Screen: Alltime AOV",
        defaultMessage:
          "Total value of all orders - including taxes, shipping, and discounts, without subtracting the value of returns.",
      });
    case MetricTypes.THREE_MONTH_LTV:
      return customerValueStr(3);
    case MetricTypes.SIX_MONTH_LTV:
      return customerValueStr(6);
    case MetricTypes.TWELVE_MONTH_LTV:
      return customerValueStr(12);
  }
};

const getSummaryPanelTitle = (intl: IntlShape, summaryPanelTitle: MetricTypes, monthly: boolean) => {
  switch (summaryPanelTitle) {
    case MetricTypes.ALLTIME_CAC:
      return intl.formatMessage({ id: "Consumers Screen: CAC", defaultMessage: "Customer Acquisition Cost" });
    case MetricTypes.ALLTIME_AOV:
      return intl.formatMessage({ id: "Consumers Screen: AOV", defaultMessage: "Average Order Value" });
    case MetricTypes.THREE_MONTH_LTV:
      return `${monthly ? "3M" : "1Q"} Customer Value`;
    case MetricTypes.SIX_MONTH_LTV:
      return `${monthly ? "6M" : "2Q"} Customer Value`;
    case MetricTypes.TWELVE_MONTH_LTV:
      return `${monthly ? "12M" : "4Q"} Customer Value`;
    default:
      return summaryPanelTitle;
  }
};

export const getFullFromShortDate = (shortDate: string) => {
  return moment(shortDate, "YYYY - M").format("YYYY-MM-DD");
};
const ltvSummaryChartDateFormat = "MMM-YYYY";
const quarterFormat = (dataPointDay: string) => {
  return `Q${moment(dataPointDay).quarter()}-${moment(dataPointDay).format("YYYY")}`;
};

export type ShownMetrics = {
  [MetricTypes.ALLTIME_AOV]: boolean;
  [MetricTypes.ALLTIME_CAC]: boolean;
  [MetricTypes.THREE_MONTH_LTV]: boolean;
  [MetricTypes.SIX_MONTH_LTV]: boolean;
  [MetricTypes.TWELVE_MONTH_LTV]: boolean;
};

export type ChartDateRange = { start: string; end: string }; // YYYY-MM-DD

export const getChartCohortDateRangeData = (
  processedCohortData: MultiChartDataPoint[],
  chartTimeRange: ChartDateRange,
  monthly: boolean
): MultiChartDataPoint[] => {
  let indexDataPointStart = 0;
  let indexDataPointEnd = processedCohortData.length - 1;
  if (moment(chartTimeRange.start).isSameOrBefore(chartTimeRange.end)) {
    processedCohortData.forEach((dataPoint, index) => {
      if (monthly) {
        if (dataPoint.day === moment(chartTimeRange.start).format(ltvSummaryChartDateFormat)) {
          indexDataPointStart = index;
        }
        if (dataPoint.day === moment(chartTimeRange.end).format(ltvSummaryChartDateFormat)) {
          indexDataPointEnd = index;
        }
      } else {
        if (dataPoint.day === quarterFormat(chartTimeRange.start)) {
          indexDataPointStart = index;
        }
        if (dataPoint.day === quarterFormat(chartTimeRange.end)) {
          indexDataPointEnd = index;
        }
      }
    });
    return processedCohortData.slice(indexDataPointStart, indexDataPointEnd + 1);
  }
  return [];
};

const summaryChartPanelActualMetrics = [
  MetricTypes.ALLTIME_AOV,
  MetricTypes.ALLTIME_CAC,
  MetricTypes.THREE_MONTH_LTV,
  MetricTypes.SIX_MONTH_LTV,
  MetricTypes.TWELVE_MONTH_LTV,
];

export const summaryChartPanelProjectedMetrics = [
  MetricTypes.FOUR_MONTH_LTV,
  MetricTypes.SEVEN_MONTH_LTV,
  MetricTypes.THIRTEEN_MONTH_LTV,
];

const summaryChartPanelAllMetrics = summaryChartPanelActualMetrics.concat(summaryChartPanelProjectedMetrics);

export const generateSummaryPanels = (
  intl: IntlShape,
  currencyCode: string,
  panelData: DataPoint,
  monthly: boolean
): SummaryPanelProps["summaryItems"] => {
  const panelProps: SummaryPanelProps["summaryItems"] = [];
  summaryChartPanelActualMetrics.forEach((panelMetric) => {
    if (panelData[panelMetric]) {
      panelProps.push({
        title: getSummaryPanelTitle(intl, panelMetric, monthly),
        metricType: panelMetric,
        metricValue: formatValue({
          value: panelData[panelMetric] as string,
          metricType: { type: "CURRENCY", currencyCode },
          narrowCurrency: isNarrowCurrency(currencyCode),
        }),
        tooltip: getSummaryTooltip(intl, panelMetric, monthly),
      });
    }
  });
  return panelProps;
};

export const getSummaryData = (
  data: DashboardOutput,
  chartTimeRange: ChartDateRange,
  shownMetrics: ShownMetrics,
  monthly: boolean
): {
  data: MultiChartDataPoint[];
  formatValue: any;
  customToolTipData: MultiChartDataPoint[];
} => {
  const chartFormatValue = (value) => {
    return formatValue({
      value: value,
      metricType: { type: "CURRENCY", currencyCode: data.currencyCode },
      narrowCurrency: isNarrowCurrency(data.currencyCode),
    });
  };
  const processedCohortData = generateCohortDataPoints(
    summaryChartPanelActualMetrics,
    summaryChartPanelProjectedMetrics,
    data,
    monthly
  );
  const chartCohortDateRangeData = getChartCohortDateRangeData(processedCohortData, chartTimeRange, monthly);

  const processedData = generateSummaryChartData(
    monthly,
    summaryChartPanelAllMetrics,
    chartCohortDateRangeData,
    shownMetrics[MetricTypes.THREE_MONTH_LTV],
    shownMetrics[MetricTypes.SIX_MONTH_LTV],
    shownMetrics[MetricTypes.TWELVE_MONTH_LTV]
  );
  return {
    data: processedData,
    formatValue: chartFormatValue,
    customToolTipData: processedCohortData,
  };
};

const computeFakeQuartiles = (averages, minLTV: number, maxLTV: number) => {
  let sum = 0;
  Object.keys(averages).forEach((a) => {
    sum += +averages[a];
  });
  const avg = sum / Object.keys(averages).length;

  return [minLTV, (avg + minLTV) / 2, (avg + maxLTV) / 2, maxLTV];
};

export const getLtvCacData = (
  intl: IntlShape,
  data: DashboardOutput,
  monthly: boolean,
  start: Moment,
  end: Moment
): ProcessedLtvCacData => {
  const { ltvData, ltvAverages, averageLtvRow, minLTV, maxLTV } = extractLTVData(data, monthly, start, end);
  const ltvQuartiles = computeFakeQuartiles(ltvAverages, minLTV, maxLTV);
  const augmentedLtvData = [averageLtvRow].concat(ltvData);

  const getTooltip = getDataTooltipFn(
    monthly ? TempRes.Month : TempRes.Quarter,
    getNumMonthMap(intl),
    data.currencyCode
  );
  const ltvCols = getLtvCols(ltvQuartiles, ltvAverages, monthly, getTooltip);

  const { cacData, averageCacRow } = extractCACData(data, monthly);
  const augmentedCacData = [averageCacRow].concat(cacData);
  const cacCols = getCacCols(intl);

  const columns = cacCols.concat(ltvCols);

  augmentedLtvData.forEach((row, index) => {
    Object.keys(row).forEach((key) => {
      augmentedCacData[index][key] = row[key];
    });
  });

  return {
    columns,
    data: augmentedCacData,
    ltvAverages,
    ltvQuartiles,
  };
};

export const filterChurnData = (
  intl: IntlShape,
  data: LegacyTableRow[],
  tempRes: TempRes,
  startString: string,
  endString: string
) => {
  const filteredData = filterDataBase(
    data,
    (row: LegacyTableRow) => {
      const [year, month] = (row["day"] as string).split(" - ") as [string, string];

      let monthNum: number;
      if (tempRes === TempRes.Month) {
        // BE provides data formatted as e.g. 2021 - Jan, so need monthMap to unwind here
        const numMonthMap = getNumMonthMap(intl);
        monthNum = parseInt(Object.keys(numMonthMap).find((key) => numMonthMap[key] === month) ?? "1");
      } else {
        monthNum = parseInt(month);
      }

      return { year: parseInt(year), month: monthNum };
    },
    tempRes,
    startString,
    endString
  );

  return filteredData.map((row) => {
    // Make keys match those that flyui wants
    return {
      newCurrent: row["new"],
      newChurnedCurrent: row["new_churned"],
      churnedCurrent: row["churned"],
      existingCurrent: row["existing"],
      existingChurnedCurrent: row["existing_churned"],
      totalCurrent: row["new"] + row["existing"],
      day: row["day"],
      percent_new_churnCurrent: roundToNumDecimals(100 * row["percent_new_churn"], 1, true),
      percent_churnCurrent: roundToNumDecimals(100 * row["percent_churn"], 1, true),
      percent_existing_churnCurrent: roundToNumDecimals(100 * row["percent_existing_churn"], 1, true),
      percent_retainCurrent: roundToNumDecimals(100 * (1 - row["percent_churn"]), 1, true),
    };
  });
};

export const getDataTooltipFn = (tempRes: TempRes, monthMap: { [k: string]: string }, currencyCode: string) => {
  return (record, columnId) => {
    if (!isNaN(columnId)) {
      const currentValueStr = record[columnId];
      const fcp = record[MetricTypes.FCP];
      const sortFcp = record[getSort(MetricTypes.FCP)];
      const cac = record[MetricTypes.ALLTIME_CAC];
      const sortCac = record[getSort(MetricTypes.ALLTIME_CAC)];

      if (!currentValueStr) {
        // Off the end of the triangle
        return "";
      }

      if (!sortFcp) {
        // Average rows
        return "";
      }

      if ([MetricTypes.FCP, MetricTypes.ALLTIME_UNIQUE_CUSTOMER_COUNT, MetricTypes.ALLTIME_CAC].includes(columnId)) {
        // LHS of table - no tooltips needed
        return "";
      }

      let currentTime = "First order";

      if (columnId !== "0") {
        const [year, month] = convertStringDate(sortFcp);

        const yearMultiplier = tempRes === TempRes.Month ? 12 : 4;
        const currentTimeVal = month + parseInt(columnId) - 1 + yearMultiplier * year;
        const currentYear = Math.floor(currentTimeVal / yearMultiplier);
        const currentMonth = currentTimeVal % yearMultiplier;

        // Bit of funniness here because the above math 0 indexes the months, but everything else doesn't
        const effectiveYear = currentMonth !== 0 ? currentYear : currentYear - 1;
        const effectiveSubdivision = currentMonth !== 0 ? currentMonth : yearMultiplier;
        currentTime = getFormattedTime(effectiveYear, effectiveSubdivision, tempRes, monthMap);
      }

      const units = tempRes === TempRes.Month ? "months" : "quarters";

      const currentValue = record[getSort(columnId)];
      const profit = formatValue({
        value: Math.abs(sortCac - currentValue).toString(),
        metricType: {
          type: "CURRENCY",
          currencyCode: currencyCode,
        },
        withDecimal: false,
        narrowCurrency: isNarrowCurrency(currencyCode),
      });
      const firstOrderFirst = `On average, customers who first ordered in ${fcp} have spent ${currentValueStr} for their first purchase.`;
      const firstOrderSecond = `The average first purchase value is ${profit} below customer acquisition cost ${cac} invested in this cohort.`;
      const sameMonthFirst = `On average, customers who first ordered in ${fcp} have spent ${currentValueStr} within a month of their first purchase.`;
      const sameMonthSecond = `Accumulated customer value is ${profit} below customer acquisition cost ${cac} invested in this cohort.`;
      const otherMonthFirst = `On average, customers who first ordered in ${fcp} have spent ${currentValueStr} after ${columnId} ${units}.`;
      const otherMonthSecond = `Accumulated customer value is ${profit} below customer acquisition cost ${cac} invested in this cohort.`;

      const isProfitable = currentValue > sortCac;

      const firstSentence = (() => {
        if (columnId === "0") {
          return firstOrderFirst;
        } else if (columnId === "1") {
          return sameMonthFirst;
        } else {
          return otherMonthFirst;
        }
      })();

      const secondSentence = (() => {
        if (columnId === "0") {
          return firstOrderSecond;
        } else if (columnId === "1") {
          return sameMonthSecond;
        } else {
          return otherMonthSecond;
        }
      })();

      const currentTimeElement = <BoldDiv>{currentTime}</BoldDiv>;

      if (isProfitable) {
        return (
          <>
            {currentTimeElement}
            <br />
            {firstSentence}
          </>
        );
      } else {
        return (
          <>
            {currentTimeElement}
            <br />
            {firstSentence}
            <br />
            <br />
            {secondSentence}
          </>
        );
      }
    }
  };
};
